import type { VariableDef } from '@holloway/dsl';
import { Variable } from '@/services/variables.api';

/** A variable as offered to the mention picker / pickers: GUID id + display label. */
export interface VariablePickerItem {
  id: string; // GUID — the durable key
  label: string; // display: "PageTitle.name" for page vars, else "name"
  scope: string; // lowercased scope
  type: string;
}

const dbTypeToDsl = (t: string): VariableDef['type'] =>
  t === 'NUMBER' ? 'number' : t === 'BOOLEAN' ? 'boolean' : 'string';

const dbScopeToDsl = (s: string): VariableDef['scope'] =>
  s === 'GLOBAL' ? 'global' : s === 'PAGE' ? 'page' : 'flow';

/** Map DB variables to the GUID-keyed DSL definitions embedded in a saved flow. */
export function toVariableDefs(vars: Variable[]): VariableDef[] {
  return vars.map((v) => ({
    id: v.id,
    name: v.name,
    type: dbTypeToDsl(v.type),
    scope: dbScopeToDsl(v.scope),
    pageNodeId: v.pageNodeId ?? undefined,
    defaultValue: v.defaultValue ?? undefined,
  }));
}

/**
 * Display label for a variable. Page variables are prefixed with their owning
 * page's current title (resolved live, so renaming the page or the variable is
 * always reflected and never breaks the underlying GUID reference).
 */
export function variableLabel(v: Variable, pageTitleById: Map<string, string>): string {
  if (v.scope === 'PAGE' && v.pageNodeId) {
    const page = pageTitleById.get(v.pageNodeId) || 'Page';
    return `${page}.${v.name}`;
  }
  return v.name;
}

/** Build the GUID-keyed mention/picker list with display labels. */
export function toPickerItems(vars: Variable[], pageTitleById: Map<string, string>): VariablePickerItem[] {
  return vars.map((v) => ({
    id: v.id,
    label: variableLabel(v, pageTitleById),
    scope: v.scope.toLowerCase(),
    type: v.type,
  }));
}

/** The persisted reference token for free-text templates. */
export const varToken = (id: string) => `{{var:${id}}}`;
